import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CheckIcon from "@mui/icons-material/Check";
import { keyframes } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Brand } from "./Brand";
import { AnimatedTitle } from "./AnimatedTitle";
import { brand, marcaFija, motion, timing, variablesClaras, velo } from "../theme/theme";

/**
 * Barrido de Atlas: el violeta entra de izquierda a derecha con el mismo
 * corte en diagonal que el panel de marca del acceso.
 */
const reveal = keyframes`
  from { clip-path: polygon(0 0, 0 0, -14% 100%, 0 100%); }
  to   { clip-path: polygon(0 0, 114% 0, 100% 100%, 0 100%); }
`;

const rise = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: none; }
`;

/**
 * El sello de confirmación llega, no brota.
 *
 * Arrancaba en `scale(0.3)`: un objeto que crece desde casi la nada se lee
 * como un efecto de sistema, no como algo que aparece. Desde 0,92 con un
 * pequeño exceso al final se lee como que cae en su sitio.
 */
const pop = keyframes`
  0%   { transform: scale(0.92); opacity: 0; }
  60%  { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1);    opacity: 1; }
`;

/**
 * Confirmación del alta.
 *
 * Ocupa la ventana completa en lugar de media: una vez enviada la solicitud
 * ya no hay nada que elegir, así que la división en dos paneles sobra y el
 * mensaje se quedaba pequeño en una mitad vacía.
 */
export function RegistrationSuccess({ onBack }) {
  const { t } = useTranslation();

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: (theme) => theme.zIndex.modal,
        // El acceso declara la paleta solo en su propio panel, no en :root:
        // fuera de él `var(--marca-violet)` no existe y la capa salía
        // transparente, dejando el formulario a la vista. La capa trae sus
        // propias variables y un fondo literal.
        ...variablesClaras,
        bgcolor: marcaFija.violet,
        // La barra «/» del título va en magenta, como en el acceso.
        "& .adl-title-prefix": { color: marcaFija.teal },
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: 3,
        py: 4,
        overflow: "hidden",
        animation: `${reveal} 700ms cubic-bezier(.77,0,.175,1) both`,
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}
    >
      {/* Mismos anillos que el panel de marca, para no romper la continuidad. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          width: 620,
          height: 620,
          right: -260,
          bottom: -240,
          borderRadius: "50%",
          border: "1px solid rgba(255,255,255,0.06)",
          boxShadow:
            "0 0 0 90px rgba(255,255,255,0.025), 0 0 0 180px rgba(255,255,255,0.015)",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "relative",
          maxWidth: 520,
          maxHeight: "100%",
          overflowY: "auto",
          animation: `${rise} ${timing.escena}ms ${motion.entrada} 220ms both`,
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      >
        {/* Sin símbolo: el nombre a gran tamaño sostiene la composición y
            el check de abajo ya aporta el elemento gráfico. */}
        <Box sx={{ display: "inline-flex", mb: { xs: 3.5, sm: 4.5 } }}>
          <Brand lettermark onDark scale={1.5} />
        </Box>

        <Box
          sx={{
            width: { xs: 64, sm: 78 },
            height: { xs: 64, sm: 78 },
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: brand.teal,
            color: "#fff",
            mx: "auto",
            mb: { xs: 2.5, sm: 3 },
            boxShadow: `0 10px 30px ${velo(brand.teal, 35)}`,
            animation: `${pop} 420ms ${motion.entrada} 340ms both`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        >
          <CheckIcon sx={{ fontSize: 42 }} />
        </Box>

        <Typography variant="h1" sx={{ color: "#fff", mb: 1.5 }}>
          <AnimatedTitle text={t("auth.registeredTitle")} brandMarks />
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: "rgba(255,255,255,0.82)",
            mb: { xs: 3, sm: 4 },
            mx: "auto",
            maxWidth: 440,
          }}
        >
          {t("auth.registrationReceived")}
        </Typography>

        <Button
          variant="outlined"
          startIcon={<ChevronLeftIcon />}
          onClick={onBack}
          sx={{
            // «&&» gana al botón contorneado del tema del acceso, que lo
            // pintaba en violeta sobre el fondo violeta (invisible).
            "&&": {
              color: "#fff",
              borderColor: "rgba(255,255,255,0.55)",
              px: 3.5,
            },
            "&&:hover": {
              borderColor: "#fff",
              bgcolor: "rgba(255,255,255,0.1)",
            },
          }}
        >
          {t("auth.backToSignIn")}
        </Button>
      </Box>
    </Box>
  );
}
